import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';

const fmtCFA = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' CFA';

const LowStockDashboard = () => {
    const { authFetch, selectedDepo, depos, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin' || user?.role_name === 'Admin' || user?.role_name === 'Super Admin';

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState({});
    const [showPOModal, setShowPOModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferSourceDepo, setTransferSourceDepo] = useState('');
    const [supplierName, setSupplierName] = useState('');
    const [generating, setGenerating] = useState(false);
    const [toast, setToast] = useState(null);

    // For admin viewing globally, default to first branch (primary branch)
    const [activeBranch, setActiveBranch] = useState(null);

    // Set activeBranch: non-admin uses their fixed depo; admin defaults to first depo
    useEffect(() => {
        if (!isAdmin) {
            // Non-admin: always their assigned branch
            setActiveBranch(selectedDepo === 'Global' ? null : selectedDepo);
        } else if (selectedDepo !== 'Global') {
            // Admin has a specific branch selected in sidebar
            setActiveBranch(selectedDepo);
        } else if (depos.length > 0) {
            // Admin on Global view → default to first/primary branch
            setActiveBranch(depos[0].id);
        }
    }, [selectedDepo, depos, isAdmin]);

    const fetchLowStock = () => {
        if (!activeBranch) return; // Wait until branch is resolved
        setLoading(true);
        authFetch(`/api/low_stock?depo_id=${activeBranch}`)
            .then(r => r.json())
            .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(() => { if (activeBranch) fetchLowStock(); }, [activeBranch]);

    const activeBranchName = depos.find(d => String(d.id) === String(activeBranch))?.name || `Branch ${activeBranch}`;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const toggleSelect = (id, item) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[id]) {
                delete next[id];
            } else {
                next[id] = {
                    product_id: item.id,
                    product_name: item.name,
                    ordered_qty: Math.max(1, item.shortage || 1),
                    unit_cost: parseFloat(item.wholesale_price || item.price || 0)
                };
            }
            return next;
        });
    };

    const updateQty = (id, qty) => {
        setSelectedItems(prev => ({ ...prev, [id]: { ...prev[id], ordered_qty: parseFloat(qty) || 0 } }));
    };

    const updateCost = (id, cost) => {
        setSelectedItems(prev => ({ ...prev, [id]: { ...prev[id], unit_cost: parseFloat(cost) || 0 } }));
    };

    const selectedList = Object.values(selectedItems);
    const totalPOValue = selectedList.reduce((sum, i) => sum + (i.ordered_qty * i.unit_cost), 0);

    const handleGeneratePO = async () => {
        if (selectedList.length === 0) { showToast(t('Select at least one item'), 'error'); return; }
        setGenerating(true);
        try {
            const res = await authFetch('/api/purchase_orders', {
                method: 'POST',
                body: JSON.stringify({ items: selectedList, supplier_name: supplierName || 'Auto-Generated PO' })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`✅ ${t('Purchase Order')} ${data.po_number} ${t('created!')}`, 'success');
                setShowPOModal(false);
                setSelectedItems({});
            } else {
                showToast(data.message || t('Failed to create PO'), 'error');
            }
        } catch (e) {
            showToast(t('Network error'), 'error');
        }
        setGenerating(false);
    };

    const handleGenerateTransfer = async () => {
        if (selectedList.length === 0) { showToast(t('Select at least one item'), 'error'); return; }
        if (!transferSourceDepo) { showToast(t('Select a source branch'), 'error'); return; }
        
        const transferItems = selectedList.filter(item => item.ordered_qty > 0).map(item => ({
            product_id: item.product_id,
            quantity: item.ordered_qty
        }));
        
        if (transferItems.length === 0) { showToast(t('Enter quantities to transfer'), 'error'); return; }

        setGenerating(true);
        try {
            const res = await authFetch('/api/transfers', {
                method: 'POST',
                body: JSON.stringify({
                    from_depo_id: transferSourceDepo,
                    to_depo_id: activeBranch,
                    items: transferItems
                })
            });
            if (res.ok) {
                showToast(`✅ ${t('Transfer created successfully!')}`, 'success');
                setShowTransferModal(false);
                setSelectedItems({});
                fetchLowStock();
            } else {
                let msg = t('Failed to create transfer');
                try { const data = await res.json(); msg = data.message || msg; } catch {}
                showToast(msg, 'error');
            }
        } catch (e) {
            showToast(t('Network error'), 'error');
        }
        setGenerating(false);
    };

    const openTransferModal = () => {
        // Auto-detect the best source branch (the one that has the most total stock for selected items)
        let bestDepo = '';
        const depoStockCounts = {};
        
        selectedList.forEach(item => {
            const originalItem = items.find(i => i.id === item.product_id);
            if (originalItem && originalItem.other_depots_stock) {
                originalItem.other_depots_stock.forEach(ods => {
                    depoStockCounts[ods.depo_id] = (depoStockCounts[ods.depo_id] || 0) + parseFloat(ods.quantity);
                });
            }
        });
        
        const sortedDepos = Object.entries(depoStockCounts).sort((a, b) => b[1] - a[1]);
        if (sortedDepos.length > 0) {
            bestDepo = sortedDepos[0][0]; // First element is the one with the highest count
        }
        
        setTransferSourceDepo(bestDepo);
        setShowTransferModal(true);
    };

    const urgencyLevel = (shortage) => {
        if (shortage >= 20) return { color: 'border-l-red-500 bg-red-50', badge: 'bg-red-500', label: t('Critical') };
        if (shortage >= 10) return { color: 'border-l-orange-400 bg-orange-50', badge: 'bg-orange-400', label: t('Low') };
        return { color: 'border-l-yellow-400 bg-yellow-50', badge: 'bg-yellow-400', label: t('Watch') };
    };

    return (
        <div className="space-y-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl font-bold text-white transition-all ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}>
                    {toast.msg}
                </div>
            )}

            <header className="flex justify-between items-start">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <span className="text-3xl">📦</span> {t('Low Stock Alerts')}
                    </h2>
                    <p className="text-gray-500 mt-1">{t('Products below their reorder threshold. Generate a Purchase Order in one click.')}</p>
                </div>
                <div className="flex gap-3 items-center">
                    {/* Branch selector — visible to Admin only when viewing globally */}
                    {isAdmin && depos.length > 1 && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-500">🏪 {t('Branch')}:</span>
                            <select
                                value={activeBranch || ''}
                                onChange={e => { setActiveBranch(e.target.value); setSelectedItems({}); }}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 bg-white shadow-sm focus:ring-2 focus:ring-indigo-400 cursor-pointer"
                            >
                                {depos.map(d => (
                                    <option key={d.id} value={d.id}>{d.name} (ID: {d.id})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button onClick={fetchLowStock} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 transition">
                        🔄 {t('Refresh')}
                    </button>
                    {Object.keys(selectedItems).length > 0 && (
                        <div className="flex gap-2">
                            <button onClick={openTransferModal} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg transition flex items-center gap-2">
                                🚚 {t('Transfer')} ({Object.keys(selectedItems).length})
                            </button>
                            <button onClick={() => setShowPOModal(true)} className="px-5 py-2 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition flex items-center gap-2">
                                📋 {t('Generate PO')}
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {/* Active branch indicator */}
            {activeBranch && (
                <div className="flex items-center gap-2 text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2">
                    <span>🏪</span>
                    <span>{t('Showing stock for')}: <strong>{activeBranchName}</strong></span>
                </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: t('Total Low-Stock Items'), value: items.length, color: 'from-red-500 to-red-600' },
                    { label: t('Selected for PO'), value: Object.keys(selectedItems).length, color: 'from-indigo-500 to-indigo-600' },
                    { label: t('Estimated PO Value'), value: fmtCFA(totalPOValue), color: 'from-emerald-500 to-emerald-600' },
                ].map(card => (
                    <div key={card.label} className={`bg-gradient-to-br ${card.color} text-white rounded-xl p-5 shadow-md`}>
                        <p className="text-sm font-medium opacity-80">{card.label}</p>
                        <p className="text-3xl font-bold mt-1">{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="text-center py-16 text-gray-400">{t('Loading...')}</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-5xl mb-4">✅</div>
                        <p className="text-xl font-bold text-gray-700">{t('All stock levels are healthy!')}</p>
                        <p className="text-gray-400 mt-1">{t('Set reorder levels on your products to enable alerts.')}</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {items.map(item => {
                            const u = urgencyLevel(item.shortage);
                            const isSelected = !!selectedItems[item.id];
                            return (
                                <div key={item.id} className={`flex items-center gap-4 p-4 border-l-4 transition-all cursor-pointer ${u.color} ${isSelected ? 'ring-2 ring-indigo-400 ring-inset' : ''}`}
                                    onClick={() => toggleSelect(item.id, item)}>
                                    <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-5 h-5 cursor-pointer" />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                            <span className={`${u.badge} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>{u.label}</span>
                                            <p className="font-bold text-gray-800">{item.name}</p>
                                            <span className="text-xs text-gray-500 font-mono">{item.sku}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            {t('In stock:')} <span className="font-bold text-red-600">{parseFloat(item.total_stock)}</span> | 
                                            {t(' Reorder at:')} <span className="font-bold">{item.reorder_level}</span> | 
                                            {t(' Shortage:')} <span className="font-bold text-red-700">{Math.max(0, item.shortage)}</span>
                                        </p>
                                        {item.other_depots_stock && item.other_depots_stock.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {item.other_depots_stock.map(ods => (
                                                    <span key={ods.depo_id} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-medium border border-blue-100 shadow-sm flex items-center gap-1">
                                                        <span className="opacity-75">🏪 {ods.depo_name}:</span> <span className="font-black">{ods.quantity}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {isSelected && (
                                        <div className="flex gap-3 items-center" onClick={e => e.stopPropagation()}>
                                            <div>
                                                <label className="text-xs text-gray-500 font-bold">{t('Qty to Order')}</label>
                                                <input type="number" min="1" value={selectedItems[item.id]?.ordered_qty || ''} 
                                                    onChange={e => updateQty(item.id, e.target.value)}
                                                    className="block w-24 border rounded-lg p-1.5 text-sm font-bold mt-1 focus:ring-2 focus:ring-indigo-400" />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-bold">{t('Unit Cost (CFA)')}</label>
                                                <input type="number" min="0" value={selectedItems[item.id]?.unit_cost || ''} 
                                                    onChange={e => updateCost(item.id, e.target.value)}
                                                    className="block w-32 border rounded-lg p-1.5 text-sm mt-1 focus:ring-2 focus:ring-indigo-400" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-right">
                                        <p className="font-bold text-gray-700">{fmtCFA(item.price)}</p>
                                        <p className="text-xs text-gray-400">{t('Sell price')}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Generate PO Modal */}
            {showPOModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h3 className="text-xl font-bold text-gray-800">📋 {t('Confirm Purchase Order')}</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{t('Supplier Name')}</label>
                                <input type="text" value={supplierName} onChange={e => setSupplierName(e.target.value)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    placeholder={t('e.g. Main Distributor Co.')} />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-56 overflow-y-auto">
                                {selectedList.map(item => (
                                    <div key={item.product_id} className="flex justify-between text-sm">
                                        <span className="font-medium text-gray-700">{item.product_name}</span>
                                        <span className="font-bold">{item.ordered_qty} × {fmtCFA(item.unit_cost)} = {fmtCFA(item.ordered_qty * item.unit_cost)}</span>
                                    </div>
                                ))}
                                <div className="border-t pt-2 flex justify-between font-bold text-base">
                                    <span>{t('Total')}</span>
                                    <span className="text-indigo-600">{fmtCFA(totalPOValue)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-3">
                            <button onClick={() => setShowPOModal(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200">{t('Cancel')}</button>
                            <button onClick={handleGeneratePO} disabled={generating} className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 shadow disabled:opacity-60 flex items-center gap-2">
                                {generating && <span className="animate-spin">⏳</span>}
                                {t('Create PO')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Modal */}
            {showTransferModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-800">🚚 {t('Transfer Stock')}</h3>
                            <button onClick={() => setShowTransferModal(false)} className="text-gray-400 hover:text-red-500 text-2xl font-bold">&times;</button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl text-sm mb-4">
                                <strong>{t('Destination')}:</strong> {activeBranchName}
                            </div>
                            
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{t('Source Branch (Transfer From)')}</label>
                                <select 
                                    value={transferSourceDepo} 
                                    onChange={e => setTransferSourceDepo(e.target.value)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="">{t('-- Select Source Branch --')}</option>
                                    {depos.filter(d => String(d.id) !== String(activeBranch)).map(d => (
                                        <option key={d.id} value={d.id}>{d.name} (ID: {d.id})</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
                                <h4 className="font-bold text-gray-700 mb-2 border-b pb-2">{t('Items to Transfer')}</h4>
                                {selectedList.filter(item => {
                                    if (!transferSourceDepo) return true;
                                    const originalItem = items.find(i => i.id === item.product_id);
                                    const sourceStock = originalItem?.other_depots_stock?.find(ods => String(ods.depo_id) === String(transferSourceDepo))?.quantity || 0;
                                    return sourceStock > 0;
                                }).map(item => {
                                    // Find stock in the selected source branch
                                    const originalItem = items.find(i => i.id === item.product_id);
                                    const sourceStock = originalItem?.other_depots_stock?.find(ods => String(ods.depo_id) === String(transferSourceDepo))?.quantity || 0;
                                    
                                    return (
                                        <div key={item.product_id} className="flex justify-between items-center bg-white p-3 border rounded-lg shadow-sm">
                                            <div className="flex-1">
                                                <p className="font-bold text-gray-800">{item.product_name}</p>
                                                <p className={`text-xs ${sourceStock > 0 ? 'text-emerald-600' : 'text-red-500 font-bold'}`}>
                                                    {transferSourceDepo ? `${t('Available')}: ${sourceStock}` : t('Select source branch')}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs font-bold text-gray-500">{t('Qty')}</label>
                                                <input 
                                                    type="number" 
                                                    min="1" 
                                                    value={item.ordered_qty} 
                                                    onChange={e => updateQty(item.product_id, e.target.value)}
                                                    className={`w-20 border p-2 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 ${item.ordered_qty > sourceStock && transferSourceDepo ? 'border-red-500 bg-red-50 text-red-700' : ''}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {transferSourceDepo && selectedList.some(item => {
                                const originalItem = items.find(i => i.id === item.product_id);
                                const sourceStock = originalItem?.other_depots_stock?.find(ods => String(ods.depo_id) === String(transferSourceDepo))?.quantity || 0;
                                return item.ordered_qty > sourceStock;
                            }) && (
                                <p className="text-red-500 text-sm font-bold mt-2">⚠️ {t('Warning: Some quantities exceed available stock in the source branch.')}</p>
                            )}
                        </div>
                        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                            <button onClick={() => setShowTransferModal(false)} className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300">{t('Cancel')}</button>
                            <button onClick={handleGenerateTransfer} disabled={generating || !transferSourceDepo} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow disabled:opacity-50 flex items-center gap-2">
                                {generating && <span className="animate-spin">⏳</span>}
                                {t('Confirm Transfer')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LowStockDashboard;
